import Corporate from '@/domain/Corporate'
import { MemberType, MemberTypes } from '@/domain/MemberType'
import { ADMIN_PROFILE_TYPES, corporateRoles, PROFILE_TYPES } from '@/domain/MyAccount'
import { hasPCRMemberType, hasTMCMemberType } from '@/domain/memberTypes'
import { userTypes } from '@/infrastructure/Configuration'
import ProfessionalDetails from '@/domain/ProfessionalDetails'
import TravelDocument from '@/domain/TravelDocument'
import { joinArrayWithComma } from '@/infrastructure/Utils'

export default class CustomerProfile {
  static fromJSON(json) {
    return new CustomerProfile(json)
  }

  constructor(json) {
    this.assignValueOrNull('title', json.title)
    this.assignValueOrNull('firstName', json.firstName)
    this.assignValueOrNull('lastName', json.lastName)
    this.assignValueOrNull('countryCode', json.customerAddress?.countryCode)
    this.assignValueOrNull('email', json.email)
    this.assignValueOrNull('programType', json.programType)
    this.assignValueOrNull('chainEnrollOrigin', json.chainEnrollOrigin)
    this.assignValueOrNull('profileType', json.profileType)
    this.assignValueOrNull('customerAddress', json.customerAddress)
    this.document = TravelDocument.fromJSON(json.document || {})
    this.assignValueOrNull('address', json.address)
    this.assignValueOrNull('postalCode', json.postalCode)
    this.assignValueOrNull('city', json.city)
    this.assignValueOrNull('state', json.state)
    this.assignValueOrNull('phone', json.phone)
    this.assignValueOrNull('tier', json.tier)
    this.assignValueOrNull('rewardsNumber', json.rewardsNumber)
    this.assignValueOrNull('language', json.language)
    this.assignValueOrNull('redeemPreferences', json.redeemPreferences)
    this.assignValueOrNull('sendPrintedMemberCard', json.sendPrintedMemberCard)
    this.assignValueOrNull('organizeMeetingsOrEvents', json.organizeMeetingsOrEvents)
    this.bookTripsForOthers = !!json.bookTripsForOthers
    this.assignValueOrNull('memberTypeExtended', json.memberTypeExtended)
    this.memberTypes = Array.isArray(json.memberTypes) ? json.memberTypes : []
    this.assignValueOrNull('travelAgent', json.travelAgent)
    this.assignValueOrNull('companyName', json.companyName)
    this.telephone = json.telephone || {}
    this.assignValueOrNull('isTravelAgent', json.isTravelAgent)
    this.assignValueOrNull('invitesLeft', json.invitesLeft)
    this.assignValueOrNull('birthDate', json.birthDate)
    this.assignValueOrNull('gender', json.gender)
    this.assignValueOrNull('placeOfBirth', json.placeOfBirth)
    this.assignValueOrNull('children', json.children)
    this.assignValueOrNull('crowd', json.crowd)
    this.assignValueOrNull('favorites', json.favorites)
    this.assignValueOrNull('reasonForTravel', json.reasonForTravel)
    this.professionalDetails = ProfessionalDetails.fromJSON(json.professionalDetails || {})
    this.assignValueOrNull('memberType', json.memberType)
    this.multifactor = json.multifactor || false
    this.corporateInfo = Corporate.fromJSON(json.corporateInfo || {})
  }

  assignValueOrNull = (key, value) => {
    this[key] = value || null
  }

  /* eslint-disable lines-between-class-members */
  isB2B = () => this.programType === 'B2B'
  isB2C = () => this.programType === 'B2C'
  isCorporate = () => this.programType === 'CORP'
  isOnlyTravelAgent = () =>
    this.hasExtendedMemberType(userTypes.TRAVEL_AGENT, MemberType.TRAVEL_AGENT)
  isTravelAgentME = () =>
    this.hasExtendedMemberType(
      userTypes.TRAVEL_AGENT_MEETINGS_AND_EVENTS,
      MemberType.TRAVEL_AGENT_ME
    )
  isAnyTravelAgent = () =>
    this.isTravelAgent === true || this.isOnlyTravelAgent() || this.isTravelAgentME()
  isTravelArranger = () =>
    this.hasExtendedMemberType(userTypes.TRAVEL_ARRENGER, MemberType.TRAVEL_ARRANGER)
  isTravelArrangerME = () =>
    this.hasExtendedMemberType(
      userTypes.TRAVEL_ARRENGER_MEETINGS_AND_EVENTS,
      MemberType.TRAVEL_ARRANGER_ME
    )
  isAnyTravelArranger = () => this.isTravelArranger() || this.isTravelArrangerME()
  isMeetingsAndEvents = () =>
    this.profileType?.toUpperCase()?.split('|').includes(PROFILE_TYPES.B2B_MEETINGS_AND_EVENTS) ||
    this.hasExtendedMemberType(userTypes.MEETINGS_AND_EVENTS, MemberType.MEETINGS_AND_EVENTS)
  isAnyMeetingsAndEvents = () =>
    this.isMeetingsAndEvents() || this.isTravelArrangerME() || this.isTravelAgentME()
  isTMC = () =>
    hasTMCMemberType({ memberTypes: this.memberTypes }) ||
    this.profileType?.toUpperCase()?.includes('TMC') === true ||
    this.memberTypeExtended?.name?.toLowerCase().includes('tmc') === true
  isPCR = () =>
    hasPCRMemberType({ memberTypes: this.memberTypes }) ||
    this.profileType?.toUpperCase()?.includes('PCR') === true ||
    this.memberTypeExtended?.name?.toLowerCase().includes('pcr') === true
  isR4U = () => this.isTMC() || this.isPCR()
  isAdministrator = () => this.memberTypeExtended.name.includes(MemberType.ADMINISTRATOR)
  isDeputy = () => this.memberTypeExtended.name.includes(corporateRoles.DEPUTY)
  isBooker = () => this.memberTypeExtended.name.includes(corporateRoles.BOOKER)
  isAnyAdministrator = () =>
    ADMIN_PROFILE_TYPES.some(adminType =>
      this.profileType?.toUpperCase()?.includes(adminType)
    )
  hasExtendedMemberType = (code, name) => {
    const extendedCode = this.memberTypeExtended?.code?.toLowerCase() || ''
    const extendedName = this.memberTypeExtended?.name?.toLowerCase() || ''
    const memberTypes = this.memberTypes
      .filter(memberType => typeof memberType === 'string')
      .map(memberType => memberType.toLowerCase())
    return (
      extendedCode.includes(code) ||
      extendedName.includes(name) ||
      memberTypes.some(memberType => memberType.includes(code))
    )
  }
  /* eslint-enable lines-between-class-members */

  getProfessionalRoleLabel() {
    const extendedRole = MemberTypes.find(
      role => role.code.toLowerCase() === this.memberTypeExtended?.code?.toLowerCase()
    )
    if (extendedRole) return extendedRole.label

    const roleData = [
      this.profileType,
      this.memberTypeExtended?.code,
      this.memberTypeExtended?.name,
      ...(this.memberTypes || [])
    ]
      .filter(Boolean)
      .join('|')
      .toLowerCase()
    const hasMeetingsAndEvents = roleData.includes('meetings_and_events') ||
      roleData.includes('meetings and events') ||
      roleData.includes('b2b-me')
    const hasTravelAgent =
      roleData.includes('travel_agent') ||
      roleData.includes('travel agent') ||
      roleData.includes('b2b-ta')
    const hasTravelArranger =
      roleData.includes('travel_arranger') || roleData.includes('travel arranger')

    if (hasTravelAgent && hasMeetingsAndEvents) return 'ppTravelAgentMeRole'
    if (hasTravelArranger && hasMeetingsAndEvents) return 'ppTravelArrangerMeRole'
    if (hasTravelAgent) return 'ppTravelAgentRole'
    if (hasTravelArranger) return 'ppTravelArrangerRole'
    if (hasMeetingsAndEvents) return 'ppMeetingsAndPlannerRole'

    return undefined
  }

  getReadableCatalogValues(catalogKey, listValues) {
    const catalog = listValues[catalogKey]
    const matches = catalog.reduce(
      (acc, curr) =>
        this.professionalDetails[catalogKey]?.includes(curr.code) ? [...acc, curr.name] : acc,
      []
    )
    return joinArrayWithComma(matches)
  }

  getCatalogTypeName(catalogDocTypes) {
    return catalogDocTypes.find(type => type.code === this.document.type).name
  }
}
